import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Phone, Mail, Clock, Instagram } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tipologie = ["Ristorante", "Hotel / Albergo", "Supermercato", "Altro"];

export default function Contatti() {
  const [form, setForm] = useState({ nome: "", email: "", azienda: "", tipologia: "", messaggio: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email || !form.messaggio) {
      toast.error("Compila nome, email e messaggio.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Richiesta inviata! Ti risponderemo al più presto.");
      setForm({ nome: "", email: "", azienda: "", tipologia: "", messaggio: "" });
    } catch (err) {
      toast.error("Si è verificato un errore. Riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#1A3322]/25 py-3.5 text-lg text-[#1A3322] placeholder:text-[#4A5A4E]/50 focus:outline-none focus:border-[#C85A32] transition-colors duration-300";

  return (
    <main data-testid="contatti-page">
      <PageHero
        eyebrow="Contatti"
        title="Parliamo"
        italic="di freschezza."
        subtitle="Raccontaci la tua attività e le tue esigenze: prepareremo una proposta di fornitura su misura."
      />

      <section className="mx-auto max-w-[1400px] px-4 sm:px-8 pb-28 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-8">
        <div className="lg:col-span-5">
          <Reveal>
            <h2 className="font-serif-display text-4xl text-[#1A3322]">Dove trovarci</h2>
            <ul className="mt-8 space-y-7">
              {[
                { icon: MapPin, t: "Sede legale", d: "Via Anfiteatro 10, 37121 Verona (VR)" },
                { icon: MapPin, t: "Sede operativa", d: "Via Alva Thomas Edison 10/A" },
                { icon: Phone, t: "Telefono", d: "393 6707253", href: "tel:+393936707253" },
                { icon: Mail, t: "Email", d: "BFRUITSRL.VR@gmail.com", href: "mailto:BFRUITSRL.VR@gmail.com" },
                { icon: Mail, t: "PEC", d: "B.FRUIT.SRL@PEC.IT", href: "mailto:B.FRUIT.SRL@PEC.IT" },
                { icon: Clock, t: "Orari", d: "Lun – Ven · 04:00 – 12:00" },
                { icon: Instagram, t: "Instagram", d: "@bfruit_vr", href: "https://www.instagram.com/bfruit_vr" },
              ].map((c) => (
                <li key={c.t} className="flex items-start gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1A3322] text-[#F4F2EE] shrink-0">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#4A5A4E]">{c.t}</p>
                    {c.href ? (
                      <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="link-underline text-xl text-[#1A3322] break-all">{c.d}</a>
                    ) : (
                      <p className="text-xl text-[#1A3322]">{c.d}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form data-testid="contact-form" onSubmit={submit} className="rounded-3xl border border-[#1A3322]/10 bg-[#EAE7E1]/50 p-7 sm:p-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#4A5A4E]">Nome e Cognome *</label>
                  <input data-testid="input-nome" value={form.nome} onChange={update("nome")} className={inputCls} placeholder="Mario Rossi" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#4A5A4E]">Email *</label>
                  <input data-testid="input-email" type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="mario@ristorante.it" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#4A5A4E]">Azienda</label>
                  <input data-testid="input-azienda" value={form.azienda} onChange={update("azienda")} className={inputCls} placeholder="Nome attività" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#4A5A4E]">Tipologia</label>
                  <select data-testid="input-tipologia" value={form.tipologia} onChange={update("tipologia")} className={`${inputCls} cursor-pointer`}>
                    <option value="">Seleziona…</option>
                    {tipologie.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-6">
                <label className="text-xs uppercase tracking-[0.2em] text-[#4A5A4E]">Messaggio *</label>
                <textarea data-testid="input-messaggio" value={form.messaggio} onChange={update("messaggio")} rows={4} className={`${inputCls} resize-none`} placeholder="Raccontaci di cosa hai bisogno…" />
              </div>
              <motion.button
                data-testid="submit-contact"
                type="submit"
                disabled={loading}
                whileTap={{ scale: 0.98 }}
                className="group mt-9 inline-flex items-center gap-3 rounded-full bg-[#1A3322] text-[#F4F2EE] px-8 py-4 text-sm uppercase tracking-widest hover:bg-[#C85A32] transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? "Invio in corso…" : "Invia richiesta"}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </motion.button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
